import { Injectable } from '@angular/core';
import { Observable, pipe, of, forkJoin } from 'rxjs';
import { map, filter, tap, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngxs/store';

import { Fetch } from '@app/models/fetch';
import { Location, LocationAppliances } from '@app/models/locations/locations.model';
import { Device } from '@app/models/devices/devices.model';
import { Notification } from '@app/models/notifications/notifications.model';
import { HeaderState, ChangeLocation } from '@app/header/header.state';

@Injectable({
    providedIn: 'root',
})
export class LocationService {
    baseUrl = '/locations';

    constructor(private fetch: Fetch, private headerState: HeaderState, private store: Store) {}

    getCurrent() {
        return this.headerState.getLocation().pipe(take(1));
    }

    get(id: number) {
        return this.fetch.getOne<Location>(this.baseUrl, id).model(Location);
    }

    getSome(ids: (number | string)[]) {}

    getAll(options?: any) {
        return this.fetch.getAll<Location>(this.baseUrl, options).models(Location);
    }

    create(loc: Location) {
        return this.fetch.post<Location>('/locations', loc);
    }

    update(id: number, loc: Partial<Location>) {
        return this.fetch.patch<Location>('/locations', id, loc).pipe(
            switchMap((res) => this.headerState.getLocation()),
            switchMap((currLoc) => {
                if (id === currLoc.id) {
                    return this.get(id).pipe(switchMap((newLoc) => this.store.dispatch(new ChangeLocation(newLoc))));
                }
                return of(currLoc);
            })
        );
    }

    joinDevice(device: Device): Observable<any> {
        return this.get(device.location_id).pipe(
            map((location) => {
                device.location = location;
                return device;
            })
        );
    }

    joinDevices(devices: Device[]) {
        return this.fetch
            .getSome<Location>(
                this.baseUrl,
                devices.map((device) => device.location_id)
            )
            .models(Location)
            .pipe(
                map((locs) => {
                    for (let i = 0; i < devices.length; i++) {
                        devices[i].location = locs[i];
                    }
                    return devices;
                })
            );
    }

    joinDevicesPipe() {
        return pipe(switchMap((devices: Device[]) => this.joinDevices(devices)));
    }

    joinNotificationFromDevice(notif: Notification): Observable<any> {
        return this.get(notif.device.location_id).pipe(
            map((location) => {
                notif.location = location;
                return notif;
            })
        );
    }

    joinNotificationsFromDevicesPipe() {
        return pipe(
            filter((notifs: Notification[]) => !notifs.find((notif) => !notif.device)),
            switchMap((notifs: Notification[]) => {
                if (notifs.length === 0) {
                    return of(notifs);
                }
                return forkJoin(notifs.map((notif) => this.joinNotificationFromDevice(notif)));
            })
        );
    }

    getUsageProfileAppliances() {
        return this.fetch.get<LocationAppliances>('//location-profiles').model(LocationAppliances);
    }
}
